import { Box, Center, Heading, Spinner, Text } from 'native-base'
import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert } from '../../components/Alert'
import { Screen } from '../../components/ScreenWrapper/Screen'
import { PolicyDocument, getPolicyDocument, PolicyDocumentURL } from '../../lib/content'
import { isSuccess } from '../../lib/request'

interface TermsAndConditionsBaseProps {
  documentURL: PolicyDocumentURL
  title: string
}

export const TermsAndConditionsBase: React.FC<TermsAndConditionsBaseProps> = ({ documentURL, title }) => {
  const { t } = useTranslation()
  const [data, setData] = useState<PolicyDocument | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    async function fetchContent() {
      setIsLoading(true)

      const response = await getPolicyDocument(documentURL)

      if (!isSuccess(response)) {
        setError(t('screens.termsAndConditions.error'))
        return
      }

      const item = response.data?.data?.[0]
      if (!item) {
        setError(t('screens.termsAndConditions.error'))
        return
      }

      setData(item)
    }

    fetchContent()
      .catch(() => setError(t('screens.termsAndConditions.error')))
      .finally(() => setIsLoading(false))
  }, [documentURL, t])

  return (
    <Screen>
      {isLoading && (
        <Center height="100%">
          <Spinner />
        </Center>
      )}

      {error ? (
        <Alert status="error" autofocus>
          {error}
        </Alert>
      ) : null}

      {!isLoading && !error && data ? (
        <Fragment>
          <Heading color={'neutral.900'} size="lg" mb={6}>
            {title}
          </Heading>
          {data.attributes.section.map(section => (
            <Box key={section.id} mb={4}>
              <Heading color={'neutral.900'} size="md" accessibilityRole="header" accessibilityLevel={2} mb={2}>
                {section.title}
              </Heading>
              <Text fontSize="md">{section.content}</Text>
            </Box>
          ))}
        </Fragment>
      ) : null}
    </Screen>
  )
}
